import { IPortalCommunicationComposite } from "@/Common/Api/PortalCommunication/IPortalCommunicationComposite";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { CommunicationsUIStateInbox } from "../UI/Enums";

class PortalCommunicationComposite {
    public sharedItem: SharedItem;
    public latestEmployeeSharedItemMessage: SharedItemMessage | null;
    public latestPatientSharedItemMessage: SharedItemMessage | null;
    public numberOfSharedItemMessages: number;
    public patientNextAppointment?: Date | null;
    public patientFirstName?: string | null;
    public patientLastName?: string | null;
    public patientPreferredName?: string | null;
    public numberOfSharedItemNotes: number;

    constructor(init?: Partial<PortalCommunicationComposite>) {
        Object.assign(this, init);
    }

    public static fromResponseModel(response: IPortalCommunicationComposite): PortalCommunicationComposite {
        if (!response) {
            return null;
        }

        return new PortalCommunicationComposite({
            sharedItem: response.SharedItem,
            latestEmployeeSharedItemMessage: response.LatestEmployeeSharedItemMessage,
            latestPatientSharedItemMessage: response.LatestPatientSharedItemMessage,
            numberOfSharedItemMessages: response.NumberOfSharedItemMessages,
            patientNextAppointment: response.PatientNextAppointment,
            patientFirstName: response.PatientFirstName,
            patientLastName: response.PatientLastName,
            patientPreferredName: response.PatientPreferredName,
            numberOfSharedItemNotes: response.NumberOfSharedItemNotes
        });
    }

    public getLatestSharedItemMessage(): SharedItemMessage {
        if (!this.latestEmployeeSharedItemMessage) {
            return this.latestPatientSharedItemMessage;
        }

        if (!this.latestPatientSharedItemMessage) {
            return this.latestEmployeeSharedItemMessage;
        }

        return (this.latestEmployeeSharedItemMessage.DateCreated > this.latestPatientSharedItemMessage.DateCreated) ?
            this.latestEmployeeSharedItemMessage :
            this.latestPatientSharedItemMessage;
    }

    public getToName(inboxType: CommunicationsUIStateInbox = null): string {
        if (inboxType === CommunicationsUIStateInbox.Inbox) {
            return this.employeeName;
        }
        else if (inboxType === CommunicationsUIStateInbox.Sent) {
            return this.capitalizedPatientName;
        }

        const latestSharedItemMessage = this.getLatestSharedItemMessage();

        if (latestSharedItemMessage === this.latestPatientSharedItemMessage) {
            return this.employeeName;
        }

        return this.capitalizedPatientName;
    }

    public getFromName(inboxType: CommunicationsUIStateInbox = null): string {
        if (inboxType === CommunicationsUIStateInbox.Inbox) {
            return this.capitalizedPatientName;
        }
        else if (inboxType === CommunicationsUIStateInbox.Sent) {
            return this.employeeName;
        }

        const latestSharedItemMessage = this.getLatestSharedItemMessage();

        if (latestSharedItemMessage === this.latestPatientSharedItemMessage) {
            return this.capitalizedPatientName;
        }

        return this.employeeName;
    }

    private get employeeName(): string {
        const employeeContactId = this.latestEmployeeSharedItemMessage?.SentByContactId ?? this.sharedItem.EmployeeContactId;
        const employee = cacheStore.GetEmployeeByContactIdOrDefault(employeeContactId);

        if (!employee) {
            return "";
        }

        return NameHelper.formatFullNameWithPrefixForHasName({
            FirstName: employee.FirstName,
            LastName: employee.LastName,
            PrefixLookupId: employee.PrefixLookupId
        }, NameHelperFormat.FirstNameFirst);
    }

    public get subjectWithNumberOfMessages(): string {
        if (this.numberOfSharedItemMessages <= 1) {
            return this.sharedItem.Subject;
        }

        return `${this.sharedItem.Subject ?? ""} (${this.numberOfSharedItemMessages})`;
    }

    public get capitalizedPatientName(): string {
        return NameHelper.formatFullNameForHasPatientName({
            PatientFirstName: this.patientFirstName?.toTitleCase(),
            PatientLastName: this.patientLastName?.toTitleCase(),
            PatientPreferredName: this.patientPreferredName?.toTitleCase()
        });
    }

    public get parsedPatientNextAppointment(): string {
        return LuxonDateTimeParser.parse(this.patientNextAppointment)?.toFormat("yyyy/MM/dd");
    }
}

export { PortalCommunicationComposite };
